import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';
import './Notifications.css';

interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'success' | 'info' | 'warning' | 'error';
  timestamp: string;
  read: boolean;
  category: 'collection' | 'payment' | 'system' | 'reminder';
  actionUrl?: string;
  actionText?: string;
}

interface NotificationsProps {
  maxVisible?: number;
  showAll?: boolean;
  compact?: boolean;
}

export function Notifications({ maxVisible = 5, showAll = false, compact = false }: NotificationsProps) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 실제 알림 데이터 로드
  useEffect(() => {
    const loadNotifications = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        // TODO: 실제 알림 API 연동
        // const response = await notificationAPI.getNotifications();
        // setNotifications(response.data);
        
        // 임시로 목 데이터 사용 (실제 API 구현 후 교체)
        await new Promise(resolve => setTimeout(resolve, 500));
        setNotifications(getMockNotifications(user));
      } catch (err: any) {
        console.error('알림 로딩 실패:', err);
        setError('알림을 불러오는데 실패했습니다.');
        // 에러 시에도 기본 알림 표시
        setNotifications(getDefaultNotifications(user));
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, [user]);

  // 사용자별 맞춤 알림 생성
  const getMockNotifications = (currentUser: any): Notification[] => {
    const baseNotifications: Notification[] = [
      {
        id: 1,
        title: '수거 요청 승인',
        message: '음식물 폐기물 수거 요청이 승인되었습니다.',
        type: 'success',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2시간 전
        read: false,
        category: 'collection',
        actionUrl: '/history',
        actionText: '내역 확인'
      },
      {
        id: 2,
        title: '내일 수거 예정',
        message: '내일 오전 11:00에 음식물 폐기물 수거가 예정되어 있습니다.',
        type: 'info',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(), // 4시간 전
        read: false,
        category: 'reminder',
        actionUrl: '/collection',
        actionText: '수정하기'
      }
    ];

    // 사용자별 맞춤 알림 추가
    if (currentUser.role === 'admin') {
      baseNotifications.push({
        id: 3,
        title: '시스템 관리 알림',
        message: '새로운 사용자 가입이 있습니다.',
        type: 'info',
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
        read: false,
        category: 'system',
        actionUrl: '/admin',
        actionText: '관리하기'
      });
    }

    return baseNotifications;
  };

  // 기본 알림 (에러 시 사용)
  const getDefaultNotifications = (currentUser: any): Notification[] => [
    {
      id: 1,
      title: '환영합니다',
      message: 'SSLMO 폐기물 관리 서비스에 오신 것을 환영합니다!',
      type: 'info',
      timestamp: new Date().toISOString(),
      read: false,
      category: 'system'
    }
  ];

  const formatTimestamp = (timestamp: string) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffMs = now.getTime() - notificationTime.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes}분 전`;
    } else if (diffHours < 24) {
      return `${diffHours}시간 전`;
    } else if (diffDays < 7) {
      return `${diffDays}일 전`;
    } else {
      return notificationTime.toLocaleDateString('ko-KR', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const getNotificationIcon = (type: string, category: string) => {
    const iconMap = {
      success: '✅',
      info: '📢',
      warning: '⚠️',
      error: '❌'
    };

    const categoryIconMap = {
      collection: '🥬',
      payment: '💳',
      system: '⚙️',
      reminder: '⏰'
    };

    return compact ? categoryIconMap[category as keyof typeof categoryIconMap] : iconMap[type as keyof typeof iconMap];
  };

  const markAsRead = async (id: number) => {
    try {
      // TODO: 실제 API 호출
      // await notificationAPI.markAsRead(id);
      
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, read: true }
            : notification
        )
      );
    } catch (error) {
      console.error('알림 읽음 처리 실패:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      // TODO: 실제 API 호출
      // await notificationAPI.markAllAsRead();
      
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
    } catch (error) {
      console.error('모든 알림 읽음 처리 실패:', error);
    }
  };

  const deleteNotification = async (id: number) => {
    try {
      // TODO: 실제 API 호출
      // await notificationAPI.deleteNotification(id);
      
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    } catch (error) {
      console.error('알림 삭제 실패:', error);
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.read;
    return notification.category === filter;
  });

  const displayedNotifications = showAll 
    ? filteredNotifications 
    : filteredNotifications.slice(0, maxVisible);

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="notifications-container">
        <div className="notifications-header">
          <h3>🔔 알림</h3>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notifications-container">
        <div className="notifications-header">
          <h3>🔔 알림</h3>
          <span className="error-text">로딩 실패</span>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-container">
      <div className="notifications-header">
        <h3>🔔 알림</h3>
        {unreadCount > 0 && (
          <span className="unread-badge">{unreadCount}</span>
        )}
        {!compact && (
          <div className="notification-controls">
            <button 
              onClick={markAllAsRead}
              className="mark-all-read-btn"
              disabled={unreadCount === 0}
            >
              모두 읽음
            </button>
          </div>
        )}
      </div>

      {!compact && (
        <div className="notification-filters">
          <button 
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            전체
          </button>
          <button 
            className={filter === 'unread' ? 'active' : ''}
            onClick={() => setFilter('unread')}
          >
            읽지 않음
          </button>
        </div>
      )}

      <div className="notifications-list">
        {displayedNotifications.length > 0 ? (
          displayedNotifications.map((notification) => (
            <div 
              key={notification.id} 
              className={`notification-item ${notification.read ? 'read' : 'unread'}`}
            >
              <div className="notification-icon">
                {getNotificationIcon(notification.type, notification.category)}
              </div>
              <div className="notification-content">
                <div className="notification-header">
                  <h4 className="notification-title">{notification.title}</h4>
                  <span className="notification-time">
                    {formatTimestamp(notification.timestamp)}
                  </span>
                </div>
                <p className="notification-message">{notification.message}</p>
                {notification.actionUrl && notification.actionText && (
                  <a 
                    href={notification.actionUrl} 
                    className="notification-action"
                  >
                    {notification.actionText}
                  </a>
                )}
              </div>
              <div className="notification-actions">
                {!notification.read && (
                  <button 
                    onClick={() => markAsRead(notification.id)}
                    className="mark-read-btn"
                    title="읽음 처리"
                  >
                    ✓
                  </button>
                )}
                <button 
                  onClick={() => deleteNotification(notification.id)}
                  className="delete-btn"
                  title="삭제"
                >
                  ×
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-notifications">
            <p>새로운 알림이 없습니다.</p>
          </div>
        )}
      </div>

      {!showAll && filteredNotifications.length > maxVisible && (
        <div className="view-all-notifications">
          <button onClick={() => setFilter('all')}>
            모든 알림 보기 ({filteredNotifications.length})
          </button>
        </div>
      )}
    </div>
  );
}